import {
  Alert,
  Box,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
  useMobileBreakpoint,
} from '@customink/pigment-react';
import {
  type DesignResponse,
  type Product,
  type ProjectItem,
  getMaxColorCount,
  transformSizes,
} from '@customink/project-service-react-components';
import { FormProvider, useFieldArray } from 'react-hook-form';

import { EditGroupSizesFormActions } from './EditGroupSizesFormActions';
import useEditSizesTags from './useEditSizesTags';
import { type FormValues, useGroupSizesForm } from './useGroupSizesForm';
import { GroupItem } from '../GroupItem';

import { useDeleteItems } from '@/hooks/ProjectItem/useDeleteItems';
import { useUpdateItems } from '@/hooks/ProjectItem/useUpdateItems';

type EditGroupSizesFormProps = {
  projectItems: ProjectItem[];
  products: Product[];
  onUpdate: () => void;
  design: DesignResponse;
  onClose: () => void;
};

const FORM_ID = 'group-sizes-form';

export const EditGroupSizesForm = ({
  projectItems,
  products,
  onClose,
  onUpdate,
  design,
}: EditGroupSizesFormProps) => {
  const isMobile = useMobileBreakpoint();
  const designColorCount = getMaxColorCount(design);
  const [deleteProjectItems] = useDeleteItems();
  const [updateItems, { isLoading: isUpdating, isError: isErrorUpdating }] =
    useUpdateItems();

  const {
    methods,
    productSizesMap,
    totalQuantity,
    groupMinQuantity: minQuantity,
  } = useGroupSizesForm(products, projectItems, designColorCount, design.views);

  // Analytics tags for the edit sizes (this hook also fires the modal launch tag on mount)
  const { fireModalUpdateTag, fireModalCloseTag, fireProjectItemDeleteTag } =
    useEditSizesTags({
      designCid: design.compositeId,
      totalQuantity,
      minQuantity,
      projectItems,
    });

  const { fields, remove } = useFieldArray({
    control: methods.control,
    name: 'items',
    keyName: 'id',
  });

  const handleClose = () => {
    fireModalCloseTag();
    onClose();
  };

  const onSubmit = async (data: FormValues) => {
    const updatedItems = data.items.map(({ id, sizes }) => ({
      ...projectItems.find((item) => item.id === id),
      sizes,
      sizesQuantities: transformSizes(sizes),
    }));
    fireModalUpdateTag();
    onClose();
    await updateItems(updatedItems);
    onUpdate();
  };

  const deleteProjectItem = async (fieldId: number, itemId: string) => {
    fireProjectItemDeleteTag();
    remove(fieldId);
    await deleteProjectItems([itemId]);
  };
  const designName = design?.designName?.split('_')[0];
  return (
    <>
      <DialogContent
        dividers
        sx={{
          borderTop: 'none',
          mt: 0,
          pt: isMobile ? '0.8125rem' : '1.25rem',
        }}
      >
        <FormProvider {...methods}>
          <form id={FORM_ID} onSubmit={methods.handleSubmit(onSubmit)}>
            {isErrorUpdating && (
              <Alert severity="error" sx={{ my: '1rem' }}>
                There was an error updating the items. Please try again.
              </Alert>
            )}
            <Stack gap="0.5rem">
              <Stack gap="0.5rem">
                <Typography variant="heading1">
                  How Many Do You Need?
                </Typography>
                <Typography variant="uiTextDefault">
                  Buy more, save more! Your volume discount grows as you add
                  more items.
                </Typography>
              </Stack>
              <Typography fontWeight="bold" variant="bodyLong2">
                {designName}
              </Typography>
              <Box>
                <Stack gap="1.5rem">
                  {fields.map((field, index) => (
                    <GroupItem
                      groupItems={projectItems}
                      item={projectItems[index]}
                      itemIndex={index}
                      key={field.id}
                      onDelete={(itemId) => deleteProjectItem(index, itemId)}
                      productItemData={productSizesMap[field.productColorId]}
                    />
                  ))}
                </Stack>
              </Box>
            </Stack>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions
        alignItems="center"
        flexDirection={isMobile ? 'column' : 'row'}
        gap={isMobile ? '1rem' : undefined}
        justifyContent="space-between"
        sx={{
          px: isMobile ? '1rem' : '1.5rem',
        }}
      >
        <EditGroupSizesFormActions
          compact={isMobile}
          formId={FORM_ID}
          isUpdating={isUpdating}
          minQuantity={minQuantity}
          onClose={handleClose}
          totalQuantity={totalQuantity}
        />
      </DialogActions>
    </>
  );
};
